import { z } from 'zod';
import { PublicExperienceProviderSchema } from './enums';
import { IsoTimestampSchema, ProviderAccountDbIdSchema, StripeAccountIdSchema, } from './primitives';
/**
 * Public Experiences — provider execution scope (Part 2, direct charges).
 *
 * The scope answers, for one persisted record, exactly three questions that a
 * direct-charge integration must never guess: which connected account OWNS the
 * provider objects (Customer, Checkout Session, Subscription, Invoice,
 * PaymentIntent), which merchant the money belongs to, and whether the record is
 * live or test mode. It is frozen on the record at claim time — before the first
 * provider write — and is never rewritten by a relink.
 *
 * Every provider call in Part 2 takes a scope and builds
 * `{ stripeAccount: scope.objectAccountId, idempotencyKey }` per request; there
 * is no mutable global account.
 *
 * Legacy discrimination is by `schemaVersion`, never by a missing field: a
 * `schemaVersion: 1` record is a platform-owned destination record (contract
 * 1.0.0), and a `schemaVersion: 2` record without a scope is a parse failure that
 * surfaces as `PXE-018 SCOPE_UNRESOLVED` — never a platform fallback.
 */
/**
 * `DESTINATION` exists only so a v1 record can be described by a read-side
 * adapter. Part 2 never WRITES a `DESTINATION` scope; the eligibility gate fails
 * closed instead of falling back to destination charges.
 */
export var ChargeModelEnum;
(function (ChargeModelEnum) {
    ChargeModelEnum["DIRECT"] = "DIRECT";
    ChargeModelEnum["DESTINATION"] = "DESTINATION";
})(ChargeModelEnum || (ChargeModelEnum = {}));
export const ChargeModelSchema = z.nativeEnum(ChargeModelEnum);
/**
 * `objectAccountId` — the account that owns the provider objects (for DIRECT the
 * merchant's connected account; for a DESTINATION adapter the platform account).
 * `merchantAccountId` — immutable merchant snapshot; equals `objectAccountId`
 * for DIRECT (enforced). `providerAccountDbId` — the existing
 * `All_Payment_Accounts` UUID row id, so an old `accountId` keeps its v1 meaning
 * and nothing is reinterpreted.
 */
export const ProviderExecutionScopeSchema = z
    .object({
    provider: PublicExperienceProviderSchema,
    chargeModel: ChargeModelSchema,
    objectAccountId: StripeAccountIdSchema,
    merchantAccountId: StripeAccountIdSchema,
    providerAccountDbId: ProviderAccountDbIdSchema,
    livemode: z.boolean(),
})
    .strict()
    .superRefine((scope, ctx) => {
    if (scope.chargeModel === ChargeModelEnum.DIRECT &&
        scope.merchantAccountId !== scope.objectAccountId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['merchantAccountId'],
            message: 'a DIRECT scope owns its own objects: merchantAccountId must equal objectAccountId',
        });
    }
});
export const isDirectExecutionScope = (scope) => scope.chargeModel === ChargeModelEnum.DIRECT;
/**
 * What can disagree between a stored scope and an observed provider context
 * (a webhook event's `account` / `livemode`, or a route's resolved account).
 */
export var ExecutionScopeMismatchEnum;
(function (ExecutionScopeMismatchEnum) {
    /** A DIRECT record's event carries no account, or a DESTINATION record's does. */
    ExecutionScopeMismatchEnum["CHARGE_MODEL"] = "CHARGE_MODEL";
    /** The event's account is not the object-owning account. */
    ExecutionScopeMismatchEnum["ACCOUNT"] = "ACCOUNT";
    /** Live/test mode disagrees. */
    ExecutionScopeMismatchEnum["LIVEMODE"] = "LIVEMODE";
})(ExecutionScopeMismatchEnum || (ExecutionScopeMismatchEnum = {}));
export const ExecutionScopeMismatchSchema = z.nativeEnum(ExecutionScopeMismatchEnum);
/** Finding code recorded when an event is skipped because its scope disagrees. */
export const EXECUTION_SCOPE_MISMATCH_FINDING = 'SCOPE_MISMATCH';
/**
 * Pure scope check shared by the webhook router, the reconcile path and the
 * merchant routes. A mismatch means "skip and log", never "reroute": the
 * record's scope is the authority, metadata and headers only assist.
 *
 * Rules (PLAN §6.5):
 *  - a DIRECT scope requires `account === objectAccountId` (a direct event
 *    always arrives on the Connect endpoint with `event.account`);
 *  - a DESTINATION scope (v1 read adapter) requires NO account (main endpoint);
 *  - `livemode` must agree in every case.
 */
export const checkExecutionScopeMatch = (scope, observed) => {
    const mismatches = [];
    const account = observed.account ?? null;
    if (scope.chargeModel === ChargeModelEnum.DIRECT) {
        if (account === null) {
            mismatches.push(ExecutionScopeMismatchEnum.CHARGE_MODEL);
        }
        else if (account !== scope.objectAccountId) {
            mismatches.push(ExecutionScopeMismatchEnum.ACCOUNT);
        }
    }
    else if (account !== null) {
        mismatches.push(ExecutionScopeMismatchEnum.CHARGE_MODEL);
    }
    if (observed.livemode !== scope.livemode) {
        mismatches.push(ExecutionScopeMismatchEnum.LIVEMODE);
    }
    return mismatches.length === 0
        ? { match: true }
        : { match: false, finding: EXECUTION_SCOPE_MISMATCH_FINDING, mismatches };
};
// ---------------------------------------------------------------------------
// Idempotency keys (account-, record- and step-scoped)
// ---------------------------------------------------------------------------
/**
 * Part 2 keys embed the object-owning account so a checkout claimed under one
 * scope can only ever be replayed under that scope (PLAN §6.3, §6.4). v1 keys
 * (`pce:{checkoutId}:session:v1`) are never reused.
 */
export const PXE_DIRECT_IDEMPOTENCY_PREFIX = 'pce2';
export const DirectCheckoutIdempotencyStepSchema = z.enum([
    'customer',
    'session',
]);
/** `pce2:{objectAccountId}:{checkoutId}:customer|session` */
export const buildDirectCheckoutIdempotencyKey = (objectAccountId, checkoutId, step) => `${PXE_DIRECT_IDEMPOTENCY_PREFIX}:${objectAccountId}:${checkoutId}:${step}`;
/**
 * `pce2:{objectAccountId}:{experienceId}:product:v1` for the Product and
 * `pce2:{objectAccountId}:{experienceId}:price:{revision}` for each Price.
 */
export const buildDirectCatalogIdempotencyKey = (objectAccountId, experienceId, target) => target === 'product'
    ? `${PXE_DIRECT_IDEMPOTENCY_PREFIX}:${objectAccountId}:${experienceId}:product:v1`
    : `${PXE_DIRECT_IDEMPOTENCY_PREFIX}:${objectAccountId}:${experienceId}:price:${target.priceRevision}`;
// ---------------------------------------------------------------------------
// Connected-account configuration snapshot (PLAN §6.0)
// ---------------------------------------------------------------------------
/**
 * Which creation profile produced the connected account. `LEGACY_CUSTOM` is
 * today's `type: 'custom'` account (the merchant pays Stripe fees on direct
 * charges, so direct charges are REJECTED — DEC-P2-01). `POINTCRAFT_MANAGED_DIRECT`
 * is the M1 `controller` profile (platform pays fees, collects requirements and
 * carries losses; no Stripe dashboard).
 */
export var ConnectedAccountProfileEnum;
(function (ConnectedAccountProfileEnum) {
    ConnectedAccountProfileEnum["LEGACY_CUSTOM"] = "LEGACY_CUSTOM";
    ConnectedAccountProfileEnum["POINTCRAFT_MANAGED_DIRECT"] = "POINTCRAFT_MANAGED_DIRECT";
})(ConnectedAccountProfileEnum || (ConnectedAccountProfileEnum = {}));
export const ConnectedAccountProfileSchema = z.nativeEnum(ConnectedAccountProfileEnum);
/** Stripe `controller.fees.payer` values, as strings (no SDK import). */
export const ConnectedAccountFeesPayerSchema = z.enum([
    'account',
    'application',
    'application_custom',
    'application_express',
]);
export const ConnectedAccountLossesPaymentsSchema = z.enum([
    'application',
    'stripe',
]);
export const ConnectedAccountRequirementCollectionSchema = z.enum([
    'application',
    'stripe',
]);
export const ConnectedAccountDashboardTypeSchema = z.enum([
    'full',
    'express',
    'none',
]);
/**
 * The persisted snapshot of a connected account's `controller` configuration
 * (`All_Payment_Accounts.metaData.stripe.controller`), captured at creation, on
 * `account.updated` and on admin refetch. `null` means "Stripe did not report
 * it" (an older API response), never "not applicable". A missing snapshot must
 * trigger one live retrieve, never a guess.
 */
export const ConnectedAccountConfigurationSnapshotSchema = z
    .object({
    profile: ConnectedAccountProfileSchema,
    feesPayer: ConnectedAccountFeesPayerSchema.nullable(),
    lossesPayments: ConnectedAccountLossesPaymentsSchema.nullable(),
    requirementCollection: ConnectedAccountRequirementCollectionSchema.nullable(),
    stripeDashboardType: ConnectedAccountDashboardTypeSchema.nullable(),
    capturedAt: IsoTimestampSchema,
})
    .strict();
/**
 * The only fee-payer configuration the direct-eligibility gate accepts. A
 * legacy `application_custom` account fails closed with
 * `PXE-017 reason: LEGACY_ACCOUNT_CONFIGURATION`. The gate additionally requires
 * `charges_enabled` and an active `card_payments` capability, which are not part
 * of this snapshot.
 */
export const DIRECT_ELIGIBLE_FEES_PAYERS = ['application'];
export const isDirectEligibleFeesPayer = (snapshot) => snapshot.feesPayer !== null &&
    DIRECT_ELIGIBLE_FEES_PAYERS.includes(snapshot.feesPayer);
